import { Button, Flex, Input, Modal, Select, Table } from 'antd';
import { ParcelOrderScanProp } from '../../common/scan';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DeleteOutlined } from '@ant-design/icons';
import { notifyError, notifySuccess } from '../../store/utils';
import { LineItemScanType } from '../../common/lineItem';
import { API } from '../../api';

interface StateType {
  index: number;
  barcode?: string;
  scanDate?: string;
  reason?: string;
  error?: string;
}

const ParcelOrderScanModal = ({
  visible,
  onClose,
  onReload,
  parcelStatus,
  items,
}: ParcelOrderScanProp) => {
  const [loading, setLoading] = useState(false);
  const [state, updateState] = useState<StateType[]>([
    {
      index: 0,
    },
  ]);
  const [defaultReason, setDefaultReason] = useState<string | undefined | null>(undefined);

  const close = useCallback(() => {
    updateState([{ index: 0 }]);
    onClose();
  }, []);

  const onFinish = useCallback(async () => {
    try {
      const payload: LineItemScanType = {
        status: parcelStatus.cd,
        barcodeItems: state
          .filter((item: any) => item.barcode)
          .map((item: any) => {
            return {
              barcode: item.barcode,
              reason: item.reason,
              scanDate: item.scanDate,
            };
          }),
      };
      if (parcelStatus.reasonRequired) {
        const filteredNoReason = payload.barcodeItems.filter((item: any) => item.reason);
        if (filteredNoReason.length != payload.barcodeItems.length) {
          notifyError('Шалтгаан дутуу сонгосон байна');
          return;
        }
      }
      setLoading(true);
      const {
        result: {
          data: { errorBarCodeList, successBarcodeList },
        },
      } = await API.post({ apiVersion: 'api' })(`/order/status`, payload);
      let list = state.filter((item: any) => (successBarcodeList || []).indexOf(item.barcode) < 0);
      list = list.map((item: any, index: number) => {
        const errorBarCode = errorBarCodeList.find((bc: any) => bc.barcode === item.barcode);
        return {
          ...item,
          error: errorBarCode?.message,
          index,
        };
      });
      updateState(list);
      notifySuccess(`Амжилттай захиалга: ${successBarcodeList.length}`);
      if (successBarcodeList.length) {
        onReload();
      }
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  }, [state, parcelStatus]);

  const changeHandler = useCallback(
    (event: any, row: any) => {
      const value = event.target.value;
      if (value.length <= 13) {
        const list = state.map((item: any) => {
          if (item.index === row.index) {
            let itm = item;
            itm = {
              ...item,
              barcode: value.toUpperCase(),
              scanDate: dayjs(),
            };
            if (!itm.reason && defaultReason) {
              itm = {
                ...itm,
                reason: defaultReason,
              };
            }
            console.log(itm);
            return itm;
          } else {
            return item;
          }
        });
        if (value.length === 13) {
          list.push({
            index: list.length,
          });
        }
        updateState(list);
      }
    },
    [state, defaultReason]
  );

  const onChangeReason = useCallback(
    (value: string, row: any) => {
      const list = state.map((item: any) => {
        if (item.index === row.index) {
          return {
            ...item,
            reason: value,
          };
        } else {
          return item;
        }
      });
      updateState(list);
    },
    [state]
  );

  const opitons = useMemo(() => {
    const shipmentStatusReasons = parcelStatus?.shipmentStatusReasons || [];
    return shipmentStatusReasons.map((item: any) => {
      return {
        value: item.cd,
        label: item.name,
      };
    });
  }, [parcelStatus]);

  useEffect(() => {
    if (visible && items?.length) {
      updateState(items);
    }
  }, [visible, items]);

  const columns = useMemo(() => {
    return [
      {
        title: '#',
        key: 'index',
        dataIndex: 'index',
        width: 50,
        render: (value: number, row: any) => <span>{value + 1}</span>,
      },
      {
        title: 'barcode',
        width: 160,
        render: (row: any) => (
          <span>
            {row.barcode && row.barcode.length == 13 ? (
              <Flex vertical>
                <div>{row.barcode}</div>
                {row?.error ? <div style={{ color: 'red', fontSize: 10 }}>{row?.error}</div> : null}
              </Flex>
            ) : (
              <Input
                value={row.code}
                onChange={(event) => changeHandler(event, row)}
                //onKeyDown={(event) => scanInput(event, row)}
                autoFocus={true}
                placeholder="Баркод оруулна уу"
              />
            )}
          </span>
        ),
      },
      {
        title: 'scanDate',
        key: 'scanDate',
        width: 160,
        render: (row: any) => (
          <Input
            value={row?.scanDate ? dayjs(row.scanDate).format('YYYY.MM.DD HH:mm:ss') : ''}
            disabled={true}
          />
        ),
      },
      {
        title: 'reason',
        key: 'reason',
        width: 160,
        render: (row: any) => (
          <Select
            style={{ width: '100%' }}
            allowClear
            value={row.reason}
            onChange={(value: string) => onChangeReason(value, row)}
            options={opitons}
          />
        ),
      },
      {
        title: 'action',
        key: 'action',
        render: (row: any) =>
          row.barcode ? (
            <Button onClick={() => deleteItem(row)} danger icon={<DeleteOutlined />} />
          ) : (
            ''
          ),
      },
    ];
  }, [parcelStatus, state, defaultReason]);

  const deleteItem = useCallback(
    (row: any) => {
      const list = state.filter((item: any) => item.index !== row.index);
      updateState(list);
    },
    [state]
  );

  return (
    <Modal
      title={`${parcelStatus?.scanName}`}
      open={visible}
      onClose={close}
      cancelText="Хаах"
      okText="Хадгалах"
      width="630px"
      onCancel={() => close()}
      footer={[
        <Flex gap={24} justify="space-between">
          <div>
            <Select
              value={defaultReason}
              onChange={(value: string) => setDefaultReason(value)}
              style={{ width: 200, textAlign: 'start' }}
              allowClear
              options={opitons}
            />
          </div>
          <Flex gap={24} justify="end">
            <Button onClick={() => close()}>Хаах</Button>
            <Button loading={loading} type="primary" onClick={() => onFinish()}>
              Хадгалах
            </Button>
          </Flex>
        </Flex>,
      ]}
    >
      <Table
        rowKey={(record) => 'scan' + record.index}
        columns={columns}
        showHeader={false}
        size={'small'}
        pagination={false}
        dataSource={state}
      />
    </Modal>
  );
};

export default ParcelOrderScanModal;
